import { Box, Stack, Group, Avatar, SimpleGrid } from '@mantine/core';
import { IconChevronDown, IconMail, IconBuilding } from '@tabler/icons-react';
import { Card, Text, Title } from '../../../../components/ui';
import { mockOrgChart, mockDepartments, OrgNode } from '../mockData/hr';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const OrgNodeCard = ({ node, level = 0 }: { node: OrgNode; level?: number }) => {
  const hasChildren = node.children && node.children.length > 0;

  return (
    <Box>
      <Card
        p="sm"
        style={{
          backgroundColor: level === 0 ? 'var(--mantine-color-blue-light)' : undefined,
        }}
      >
        <Group gap="sm">
          <Avatar size="md" radius="xl" color={level === 0 ? 'blue' : 'gray'}>
            {node.name.split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={600}>{node.name}</Text>
            <Text size="xs" c="dimmed">{node.title}</Text>
            <Text size="xs" c="dimmed">{node.department}</Text>
          </Box>
        </Group>
      </Card>

      {hasChildren && (
        <Box
          ml={40}
          pl="md"
          style={{
            borderLeft: '2px solid var(--mantine-color-gray-3)',
          }}
        >
          <Stack gap="sm" mt="sm">
            {node.children!.map((child) => (
              <OrgNodeCard key={child.id} node={child} level={level + 1} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default function OrganizationPage() {
  return (
    <Stack gap="lg">
      {/* Header */}
      <Box>
        <Title order={2}>Organization Chart</Title>
        <Text c="dimmed" size="sm">View the company structure and reporting lines</Text>
      </Box>

      {/* Department Stats */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        {mockDepartments.slice(0, 4).map(dept => (
          <Card key={dept.id}>
            <Group justify="space-between">
              <Group gap="sm">
                <Box
                  w={40}
                  h={40}
                  style={{
                    borderRadius: 8,
                    backgroundColor: `var(--mantine-color-${dept.color}-light)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconBuilding size={20} color={`var(--mantine-color-${dept.color}-filled)`} />
                </Box>
                <Box>
                  <Text size="sm" fw={500}>{dept.name}</Text>
                  <Text size="xs" c="dimmed">{dept.employeeCount} employees</Text>
                </Box>
              </Group>
            </Group>
            <Group mt="sm" justify="space-between">
              <Group gap="xs">
                <Avatar size="xs" radius="xl" color={dept.color}>
                  {dept.headName.split(' ').map(n => n[0]).join('')}
                </Avatar>
                <Text size="xs" c="dimmed">{dept.headName}</Text>
              </Group>
              <Text size="xs" c="dimmed">{formatCurrency(dept.budget)}</Text>
            </Group>
          </Card>
        ))}
      </SimpleGrid>

      {/* All Departments */}
      <Card>
        <Text fw={600} mb="md">All Departments</Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          {mockDepartments.map(dept => (
            <Box
              key={dept.id}
              p="sm"
              style={{
                backgroundColor: 'var(--mantine-color-gray-light)',
                borderRadius: 8,
              }}
            >
              <Group gap="sm">
                <Box
                  w={12}
                  h={12}
                  style={{
                    borderRadius: 4,
                    backgroundColor: `var(--mantine-color-${dept.color}-filled)`,
                  }}
                />
                <Text size="sm" fw={500}>{dept.name}</Text>
              </Group>
              <Group mt="xs" justify="space-between">
                <Text size="xs" c="dimmed">{dept.employeeCount} employees</Text>
                <Text size="xs" c="dimmed">{formatCurrency(dept.budget)}</Text>
              </Group>
            </Box>
          ))}
        </SimpleGrid>
      </Card>

      {/* Org Chart */}
      <Card>
        <Text fw={600} mb="md">Organization Structure</Text>
        <Box>
          <OrgNodeCard node={mockOrgChart} />
        </Box>
      </Card>
    </Stack>
  );
}
